import { Link } from '@tanstack/react-router';
import { PaintBucket } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, ErrorState } from '@mustauto/ui/web';
import { SOFT_BADGE_CLASSES } from '@mustauto/ui/web';
import { formatElapsed } from '@mustauto/utils';
import { Badge } from '@/components/ui/badge';
import { PAINT_BOOTH_STAGE, getStageLabel, paintBoothQueueStage } from '@mustauto/types';
import { usePaintBooth, type PaintBoothEntry } from '@/hooks/usePaintBooth';
import { useStageConfig } from '@/hooks/useSettings';

/**
 * A cabine de pintura no dashboard (F3.5) — o gargalo da funilaria.
 *
 * Tudo sai da etapa da OS (decisão do dono do produto, 24/09): "na cabine" é
 * quem está na etapa "Cabine de pintura", "próximos" é quem está na etapa
 * logo antes dela ("Preparação", no padrão), na ordem em que entrou nela.
 * Ninguém mantém a fila à mão, e por isso ela não mente.
 *
 * Com as etapas da oficina (F3.8), os dois nomes são os dela ("Estufa",
 * "Próximos · em lixamento"). Cabine escondida: a API responde
 * `enabled: false` e o card some.
 *
 * Só aparece para oficina com o módulo de funilaria. Sem cabine cadastrada o
 * card aparece mesmo assim — o carro está na etapa de qualquer jeito — e diz
 * onde cadastrar, porque é o número de cabines que dá o "1 de 1 ocupada".
 */
export function PaintBoothCard({ workshopId }: { workshopId: string | null }) {
  const { data, isError, refetch } = usePaintBooth(workshopId);
  const stageConfig = useStageConfig();
  const boothLabel = getStageLabel(PAINT_BOOTH_STAGE, stageConfig);
  const queueStage = paintBoothQueueStage(stageConfig);
  const queueLabel = queueStage ? getStageLabel(queueStage, stageConfig).toLocaleLowerCase('pt-BR') : null;

  // Até a primeira leitura não se sabe se a oficina tem funilaria — e a
  // maioria da base não tem. Esqueleto ou erro aqui apareceriam no dashboard
  // de toda oficina de mecânica, para um card que nunca vai existir para ela.
  // Depois de uma leitura boa, o react-query mantém o dado numa falha de
  // atualização; a falha só vira aviso para quem já tem o card.
  if (!data) return null;
  if (isError && data.enabled) {
    return (
      <Card>
        <CardContent className="pt-6">
          <ErrorState title="Não foi possível atualizar a cabine de pintura" onRetry={() => void refetch()} />
        </CardContent>
      </Card>
    );
  }

  if (!data?.enabled) return null;

  const capacity = data.booths.length;
  const occupied = data.inBooth.length;
  const overCapacity = capacity > 0 && occupied > capacity;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <PaintBucket size={16} aria-hidden />
          {boothLabel}
        </CardTitle>
        {capacity > 0 ? (
          <span className="text-sm text-muted-foreground">
            {Math.min(occupied, capacity)} de {capacity} {capacity === 1 ? 'ocupada' : 'ocupadas'}
          </span>
        ) : (
          <Link to="/slots" className="text-sm font-medium text-primary hover:underline">
            Cadastrar a cabine
          </Link>
        )}
      </CardHeader>
      <CardContent className="grid gap-6 md:grid-cols-2">
        <section aria-label="Na cabine">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Na cabine</h3>
          {occupied === 0 ? (
            <p className="text-sm text-muted-foreground">Cabine livre.</p>
          ) : (
            <ul className="divide-y">
              {data.inBooth.map((e) => (
                <BoothRow key={e.id} entry={e} verb="na cabine" />
              ))}
            </ul>
          )}
          {overCapacity && (
            <p className={`mt-2 rounded-md px-2 py-1 text-xs font-medium ${SOFT_BADGE_CLASSES.amber}`}>
              {occupied} carros na etapa para {capacity} {capacity === 1 ? 'cabine' : 'cabines'} — confira as etapas.
            </p>
          )}
        </section>
        <section aria-label="Próximos da cabine">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {queueLabel ? `Próximos · em ${queueLabel}` : 'Próximos'}
          </h3>
          {data.upNext.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {queueLabel ? `Nenhum carro em ${queueLabel}.` : 'Nenhum carro a caminho.'}
            </p>
          ) : (
            <ol className="divide-y">
              {data.upNext.map((e, i) => (
                <BoothRow key={e.id} entry={e} position={i + 1} verb="esperando" />
              ))}
            </ol>
          )}
        </section>
      </CardContent>
    </Card>
  );
}

function BoothRow({ entry, position, verb }: { entry: PaintBoothEntry; position?: number; verb: string }) {
  const elapsed = formatElapsed(entry.since);
  return (
    <li className="flex items-center gap-3 py-2">
      {position !== undefined && (
        <span className="w-5 text-center text-sm font-semibold text-muted-foreground">{position}</span>
      )}
      <div className="min-w-0 flex-1">
        <Link
          to="/service-orders/$osId"
          params={{ osId: entry.id }}
          className="font-mono text-sm font-semibold hover:underline"
        >
          {entry.plate}
        </Link>
        <p className="truncate text-xs text-muted-foreground">
          OS #{entry.number} · {entry.vehicleName}
          {entry.mechanicName ? ` · ${entry.mechanicName}` : ''}
        </p>
      </div>
      {entry.status === 'waiting_part' && <Badge className={SOFT_BADGE_CLASSES.yellow}>Ag. peça</Badge>}
      {entry.status === 'outsourced' && <Badge className={SOFT_BADGE_CLASSES.indigo}>Terceirizado</Badge>}
      {elapsed && (
        <span className="whitespace-nowrap text-xs text-muted-foreground" title={new Date(entry.since).toLocaleString('pt-BR')}>
          {verb} {elapsed}
        </span>
      )}
    </li>
  );
}
